/*
----------------------------------------------------------------------------
Usage:
----------------------------------------------------------------------------
yarn add axios
import Listing from './modules/listing'
_('listing').nodes().map((el) => new Listing(el))

----------------------------------------------------------------------------//
Detail:
----------------------------------------------------------------------------//
every input/select with data-element="input"  will be iterated through to add to the endpoint url using the name="category" value="news" for values in makeEndpointUrl()
e.g `apiurl/?category=news&name2=value2`

----------------------------------------------------------------------------
Attributes:
----------------------------------------------------------------------------
data-behaviour="listing"                     root element
data-behaviour="endpoint-input"              on change will take the value and do an api call regardless of other select/inputs values
data-behaviour="go"                          on click will make makeEndpointUrl
data-behaviour="go-to" data-endpoint="/url/" on click will make that api call
data-behaviour="clear-form"                  clears the form and getsResults
data-element="input"                         on change triggers makeEndpointUrl() and resets the pagination to 1 if it isn't the pagination that was the change trigger

*/

import { _, makeGtmCall, debounce, changeURLParams } from '../utils.js'
import EntranceTransition from './../modules/entrance-transitions.js'
import LazyLoad from './../modules/lazy-load.js'
import axios from 'axios'
import loading from '../templates/loading.js'
import errorMessageContainer from '../templates/error.js'
import card from '../templates/card.js'
import podcast from '../templates/podcast.js'
import event from '../templates/event.js'
import partner from '../templates/partner.js'
import caseStudy from '../templates/case-study.js'
import select from '../templates/select.js'
import service from '../templates/service.js'
import solution from '../templates/solution.js'
import sortBySelect from '../templates/sort-by-select.js'
import radioTabs from '../templates/radio-tabs.js'
import radioTags from '../templates/radio-tag.js'
import Bugsnag from '@bugsnag/js'

export default class Listing {
    constructor(el) {
        this.el = el
        this.form = _('form', 'element').nodeFrom(this.el)
        this.listingPartners =
            this.el.dataset.listingType === 'partners' ? true : false
        this.c5EditMode = this.el.dataset.c5EditMode === '1' ? true : false
        this.scrollUp = false
        this.scrollPos = document.documentElement.scrollTop
        this.firstLoad = true
        this.partnersFirstLoad = true
        this.urlCheck = this.el.dataset.urlCheck || 'true'
        this.noResultsIgnore = false
        setTimeout(() => {
            this.firstLoad = false
        }, 2000)

        this.message = {
            success: 'Thanks!',
            error: 'Something has gone quite wrong, please try to refresh your browser or contact us.',
        }

        this.pagination = {
            container: _('pagination', 'element').nodeFrom(this.el),
            input: _('pagination-input').nodeFrom(this.el),
            totalPages: _('pagination-total-pages', 'element').nodeFrom(
                this.el,
            ),
            nextPageTrigger: _('next-page').nodeFrom(this.el),
            previousPageTrigger: _('previous-page').nodeFrom(this.el),
        }

        this.container = {
            results: _('results-container', 'element').nodeFrom(this.el),
            noResults: _('no-results', 'element').nodeFrom(this.el),
            filters: _('filters-container', 'element').nodeFrom(this.el),
            sorting: _('sorting-container', 'element').nodeFrom(this.el),
            resultsRange: _('results-range-container', 'element').nodeFrom(
                this.el,
            ),
            radioTabsContainer: _('radio-tabs-container', 'element').nodeFrom(
                this.el,
            ),
            loading: _('loading-container', 'element').nodeFrom(this.el),
            partnersContainer: _('partners-container', 'element').nodeFrom(
                this.el,
            ),
            radioTags: _('radio-tag-container', 'element').nodeFrom(this.el),
            radioFieldset: _('radio-fieldset', 'element').nodeFrom(this.el),
        }

        this.input = {
            els: _('input', 'element').nodesFrom(this.el),
            endpoint: _('endpoint-input').nodeFrom(this.el),
            search: _('search-input').nodeFrom(this.el),
            clear: _('clear-form').nodeFrom(this.el),
            resetView: _('reset-view').nodeFrom(this.el)
        }

        this.partnerBlocks = _('partner-blocks', 'element').node()

        this.viewAllPartners = _('all-partners', 'element').node()

        this.searchIcon = _('search-icon', 'element').nodeFrom(this.el)

        this.goToUrls = _('go-to-url').nodesFrom(this.el)
        this.gos = _('go').nodesFrom(this.el)

        if (!this.c5EditMode) {
            this.goToUrls.forEach((goto) => {
                goto.addEventListener('click', this.setEndpointUrl)
            })

            this.gos.forEach((go) => {
                go.addEventListener('click', this.makeEndpointUrl)
            })

            if (this.input.clear) {
                this.input.clear.addEventListener('click', this.clearForm)
            }

            if (this.input.resetView) {
                this.input.resetView.addEventListener('click', this.resetView)
            }

            if (this.viewAllPartners) {
                this.viewAllPartners.addEventListener(
                    'mousedown',
                    this.showAllPartners,
                )
            }

            this.input.els.forEach((input) => {
                input.addEventListener('change', this.makeEndpointUrl)
            })

            if (this.input.search) {
                const self = this

                this.input.search.addEventListener('keydown', function () {
                    self.searchIcon.classList.add('u-hidden')
                })

                this.input.search.addEventListener(
                    'input',
                    debounce(this.search, 500),
                )
            }

            this.input.endpoint?.addEventListener('change', this.getResults)

            this.form?.addEventListener('submit', this.formSubmit)
            if (this.el.dataset.onload !== 'false') {
                this.makeEndpointUrl()
            }

            this.pagination.nextPageTrigger?.addEventListener(
                'click',
                this.goNextPage,
            )
            this.pagination.previousPageTrigger?.addEventListener(
                'click',
                this.goPreviousPage,
            )

            if (this.urlCheck === 'true') {
                this.changeInputsToParams()
            }
        }
    }

    search = (e) => {
        if (e.target.value.length > 2 || e.target.value.length === 0) {
            this.resetPaginator()
            this.makeEndpointUrl()
            this.trackEvent()
            if (this.partnerBlocks) {
                this.partnerBlocks.classList.add('u-hidden')
                this.el.classList.add('u-pb-double')
            }
            this.container.results.classList.remove('u-hidden')

            if (this.listingPartners) {
                this.pagination.container.classList.remove('u-hidden')
            }
        }
        if (e.target.value.length === 0) {
            this.searchIcon.classList.remove('u-hidden')
        }
    }

    changeInputsToParams = () => {
        const url = new URL(window.location)
        const params = new URLSearchParams(url.search)
        let paramsPresent = false

        params.forEach((value, key) => {
            paramsPresent = true
            const input = this.el.querySelector(`[name="${key}"]`)

            if (!input) {
                return false
            }
            const radioInput = this.el.querySelector(`[value="${value}"]`)

            if (input && input.type === 'checkbox') {
                input.checked = value === 'true'
            }

            if (input && input.type === 'radio') {
                radioInput.checked = 'true'
            }

            if (
                (input && input.type === 'text') ||
                input.type === 'number' ||
                input.type === 'select-one'
            ) {
                input.value = value
            }

            if (input && input.type === 'search') {
                input.value = value
            }
        })

        if (paramsPresent) {
            return this.makeEndpointUrl()
        }

        if (!paramsPresent && this.onLoad === 'true') {
            this.makeEndpointUrl()
        }
    }

    showAllPartners = async (e) => {
        if (e) {
            e.preventDefault()
        }
        if (this.listingPartners) {
            this.pagination.container.classList.remove('u-hidden')
            this.partnerBlocks.classList.add('u-hidden')
            this.container.results.classList.remove('u-hidden')
            this.container.partnersContainer.classList.add('u-mb-100')
            this.input.els.forEach((input) => {
                input.value = ''
            })
            if (!this.container.noResults.classList.contains('u-hidden')) {
                this.noResultsIgnore = true
            }
            await this.makeEndpointUrl()
        }

    }

    formSubmit = (e) => {
        e.preventDefault()
        this.makeEndpointUrl()
    }

    showLoading = () => {
        if (this.container.loading) {
            this.container.loading.innerHTML = loading()
        }
        this.container?.noResults?.classList.add('u-hidden')
        this.container.results.innerHTML = ''
    }

    hideLoading = () => {
        if (this.container.loading) {
            this.container.loading.innerHTML = ''
        }
    }

    clearForm = () => {
        this.form.reset()
    }

    setEndpointUrl = (e) => {
        this.input.endpoint.value = e.currentTarget.dataset.endpoint
        this.getResults()
    }

    goNextPage = () => {
        this.pagination.input.value++
        this.scrollUp = true
        this.makeEndpointUrl()
    }

    goPreviousPage = () => {
        this.pagination.input.value--
        this.scrollUp = true
        this.makeEndpointUrl()
    }

    resetPaginator = () => {
        if (this.pagination.input) {
            this.pagination.input.value = 1
        }
    }

    scrollToResultsTop = () => {
        if (this.scrollUp) {
            window.scrollTo({
                top:
                    this.container.results.getBoundingClientRect().top -
                    document.body.getBoundingClientRect().top -
                    300,
                behavior: 'smooth',
            })
        }
        this.scrollUp = false
    }

    // Reset Filters function?
    resetView = () => {
        if (this.partnerBlocks) {
            this.partnerBlocks.classList.remove('u-hidden')
            this.el.classList.remove('u-pb-double')
            this.container.partnersContainer.classList.remove('u-mb-100')
        }
        this.container.results.classList.add('u-hidden')

        if (this.listingPartners) {
            this.pagination.container.classList.add('u-hidden')
        }
        this.container.noResults.classList.add('u-hidden')
        this.input.els.forEach((input) => {
            input.value = ''
        })
    }

    makeEndpointUrl = (e) => {
        if (this.c5EditMode || this.input.endpoint === null) {
            return
        }

        this.changeEvent = typeof e === 'string' ? e : null
        this.startEndpoint = this.input.endpoint.value.split('?')
        this.endpoint = this.startEndpoint[0]
        const inputs = _('input', 'element').nodesFrom(this.el)

        if (inputs.length) {
            inputs.map((el, i) => {
                if (!i) {
                    this.endpoint += '?'
                }
                if (el.type !== 'radio' && el.type !== 'checkbox') {
                    if (
                        e === 'filters[event_status]' &&
                        el.getAttribute('name') === 'filters[event_date]'
                    ) {
                        this.endpoint += `&${el.getAttribute('name')}=`
                    } else {
                        this.endpoint += `&${el.getAttribute('name')}=${
                            this.noResultsIgnore ? '' : el.value
                        }`
                    }
                }
                if (el.type === 'radio' && el.checked) {
                    this.endpoint += `&${el.getAttribute('name')}=${el.value}`
                }

                if (el.dataset?.behaviour === 'preset-filter') {
                    el.remove()
                }

                // Has firstLoad delay so that the active class isn't removed from the
                // original active tab on load
                if (!this.firstLoad) {
                    if (el.type === 'radio' && !el.checked) {
                        el.parentNode.childNodes
                            .item(3)
                            .classList.remove('radio-tabs__label--active')
                    }
                }

                if (el.type === 'checkbox') {
                    this.endpoint += `&${el.getAttribute('name')}=${el.checked}`
                }
            })
            this.noResultsIgnore = false
        }

        if (this.pagination.container && !this.pagination.input.value) {
            this.endpoint += `&${this.pagination.input.getAttribute('name')}=1`
        }

        const url = new URL(window.location)
        const params = new URLSearchParams(url.search)

        if (params.has('view') && params.get('view') === 'all') {
            this.showAllPartners()
        }

        this.endpoint = this.endpoint.replace('?&', '?')
        this.input.endpoint.value = encodeURI(this.endpoint)

        if (
            !!e &&
            !!e.currentTarget &&
            e.currentTarget.dataset.behaviour === 'pagination'
        ) {
            this.getResults(true)
        } else {
            this.getResults(false)
            this.resetPaginator()
        }
    }

    getResults = async (scrollTop = false) => {
        if (this.c5EditMode) {
            return
        }

        this.showLoading()
        await axios({
            method: this.el.dataset.action || 'get',
            url: this.input.endpoint.value,
        })
            .then((response) => {
                if (this.pagination) {
                    this.buildPagination(response.data)
                }

                if (this.container.radioTabsContainer) {
                    this.buildRadioTabs(response.data)
                }

                if (this.container.filters) {
                    this.buildFilters(response.data)
                }

                if (this.container.radioTags) {
                    this.buildRadios(response.data)
                }

                if (this.container.sorting) {
                    this.buildSorting(response.data)
                }

                if (this.container.resultsRange) {
                    this.buildResultsRange(response.data)
                }

                if (response.status !== 200) {
                    return this.handleError(response)
                }

                if (
                    !response?.data.length &&
                    !Object.keys(response?.data?.data).length
                ) {
                    return this.handleNoResults()
                } else {
                    if (
                        this.listingPartners &&
                        this.partnersFirstLoad === false
                    ) {
                        this.container.results.classList.remove('u-hidden')
                        this.container.partnersContainer.classList.add(
                            'u-mb-100',
                        )

                        if (this.partnerBlocks && !this.c5EditMode) {
                            this.partnerBlocks.classList.add('u-hidden')
                        }
                    }
                }

                changeURLParams(this.input.endpoint.value)
                this.partnersFirstLoad = false

                return this.handleSuccess(response.data, scrollTop)
            })
            .catch((error) => {
                this.handleError(error)
            })
    }

    buildPagination = (response) => {
        if (
            !response.pagination ||
            !response.pagination.totalPages ||
            response.pagination.totalPages < 2
        ) {
            this.pagination.container.classList.add('u-hidden')
            return false
        } else {
            if (!this.listingPartners) {
                this.pagination.container.classList.remove('u-hidden')
            }

            if (this.listingPartners && this.partnersFirstLoad === false) {
                this.pagination.container.classList.remove('u-hidden')
            }
        }
        let paginationHTML = ''
        for (let i = 1; i <= response.pagination.totalPages; i++) {
            paginationHTML += `<option value="${i}" ${
                response.pagination.currentPage === i ? 'selected' : ''
            }>${i}</option>`
        }
        this.pagination.totalPages.innerHTML = response.pagination.totalPages
        this.pagination.input.innerHTML = paginationHTML
        this.pagination.previousPageTrigger.disabled =
            response.pagination.currentPage === 1
        this.pagination.nextPageTrigger.disabled =
            response.pagination.currentPage === response.pagination.totalPages
    }

    buildRadioTabs = (response) => {
        if (!response.filters) {
            this.container.radioTabsContainer.classList.add('u-hidden')
            return false
        } else {
            this.container.radioTabsContainer.classList.remove('u-hidden')
        }

        if (this.el.dataset.type === 'partner') {
            let radioTabsHTML = ''
            radioTabsHTML += '<div>'

            for (let i = 0; i < response.filters.length; i++) {
                if (response.filters[1]) {
                    const item = radioTabs(response.filters[1])
                    radioTabsHTML += item
                    break
                }
            }

            radioTabsHTML += '</div>'
            this.container.radioTabsContainer.innerHTML = radioTabsHTML
        } else {
            let radioTabsHTML = ''
            radioTabsHTML += '<div>'

            response.filters.forEach((el, i) => {
                const item = radioTabs(response.filters[i])
                radioTabsHTML += item
            })

            radioTabsHTML += '</div>'
            this.container.radioTabsContainer.innerHTML = radioTabsHTML
        }

        const inputs = _('input', 'element').nodesFrom(
            this.container.radioTabsContainer,
        )
        inputs.forEach((input) => {
            input.addEventListener('change', this.makeEndpointUrl)
        })
    }

    buildFilters = (response) => {
        this.input.els.forEach((input) => {
            input.addEventListener('change', this.filterChange)
        })

        if (!response.filters) {
            this.container.filters.classList.add('u-hidden')
            return false
        } else {
            this.container.filters.classList.remove('u-hidden')
        }
        if (this.el.dataset.type === 'partner') {
            let filterHTML = ''
            filterHTML += '<div class="selects">'
            for (let i = 0; i < response.filters.length; i++) {
                if (response.filters[i].options.length > 0) {
                    if (
                        response.filters[i].handle === 'related_solution' ||
                        response.filters[i].handle === 'it_priority'
                    ) {
                        const item = select(response.filters[i])
                        filterHTML += item
                    }
                }
            }

            filterHTML += '</div>'
            this.container.filters.innerHTML = filterHTML
        } else {
            let filterHTML = ''
            filterHTML += '<div class="selects">'
            const selectFiltersOnly = response.filters.filter((myObj => myObj.handle !== 'news_topics'))

            selectFiltersOnly.forEach((el, i) => {
                if ((response.filters[i].options.length > 0)) {
                    const item = select(response.filters[i])
                    filterHTML += item
                }
        })
            filterHTML += '</div>'
            this.container.filters.innerHTML = filterHTML
        }

        const inputs = _('input', 'element').nodesFrom(this.container.filters)

        inputs.forEach((input) => {
            const name = input.name
            input.addEventListener('change', () => {
            this.resetPaginator()

                this.makeEndpointUrl(name)
            })
        })

        if (this.firstLoad) {
            this.changeInputsToParams()
        }
    }

    buildRadios = (response) => {
        this.input.els.forEach((input) => {
            input.addEventListener('change', this.filterChange)
        })

        // needs tobe more specific
        if (!response.filters) {
            this.container.radioFieldset.classList.add('u-hidden')
            return false
        } else {
            this.container.radioFieldset.classList.remove('u-hidden')
        }

        let radioTagHtml = ''
        radioTagHtml += '<div class="radio-tag-group">'
        const topics = response.filters.filter((myObj => myObj.handle === 'news_topics'))
        topics.forEach((el, i) => {
            if (topics[i].options.length > 0) {
                this.container.radioFieldset.classList.remove('u-hidden')
                this.container.radioFieldset.classList.add('u-df')

                const item = radioTags(topics[i])
                radioTagHtml += item
            } else {
                this.container.radioFieldset.classList.add('u-hidden')
                 this.container.radioFieldset.classList.remove('u-df')
            }
        })

        radioTagHtml += '</div>'
        this.container.radioTags.innerHTML = radioTagHtml

        const inputs = _('input', 'element').nodesFrom(this.container.radioTags)

        inputs.forEach((input) => {
            const name = input.name
            input.addEventListener('change', () => {
                this.makeEndpointUrl(name)
            })
        })

        if (this.firstLoad) {
            this.changeInputsToParams()
        }
    }

    buildSorting = (response) => {
        if (!response.sortBy) {
            this.container.sorting.classList.add('u-hidden')
            return false
        } else {
            this.container.sorting.classList.remove('u-hidden')
        }
        let sortByHTML = ''
        sortByHTML +=
            '<label for="sort-by">Sort By</label><select class="select select--simple" id="sort-by" name="sortBy" data-element="input" data-filter="sort-by">'
        response.sortBy.forEach((el, i) => {
            const item = sortBySelect(response.sortBy[i])
            sortByHTML += item
        })
        sortByHTML += '</select>'
        this.container.sorting.innerHTML = sortByHTML

        const inputs = _('input', 'element').nodesFrom(this.container.sorting)
        inputs.forEach((input) => {
            input.addEventListener('change', this.makeEndpointUrl)
        })
    }

    buildResultsRange = (response) => {
        if (!response.pagination.resultsRangeText) {
            this.container.resultsRange.classList.add('u-hidden')
            return false
        } else {
            this.container.resultsRange.classList.remove('u-hidden')
        }
        let resultsRangeCopy = ''
        resultsRangeCopy +=
            '<div class="form__results-range"><p>' +
            response.pagination.resultsRangeText +
            '</p></div>'
        this.container.resultsRange.innerHTML = resultsRangeCopy
    }

    trackEvent = () => {
        this.el.dataset.gtmEvent = this.el.dataset.gtmEvent ?? 'listingEvent'
        this.el.dataset.gtmSearch = this.input.search.value
        makeGtmCall(this.el)
    }

    handleSuccess = (response) => {
        this.hideLoading()
        if (this.el.dataset.type === 'card') {
            const latestNews = document.querySelector(
                '[data-element="latest-news"]',
            )
            if (response.pagination.currentPage >= 2) {
                latestNews?.classList.add('u-hidden')
                latestNews?.nextElementSibling.classList.remove(
                    'section--keep-padding',
                )
            } else {
                latestNews?.classList.remove('u-hidden')
                latestNews?.nextElementSibling.classList.add(
                    'section--keep-padding',
                )
            }
            return this.handleSuccessTypeCard(response)
        }

        if (this.el.dataset.type === 'podcast') {
            return this.handleSuccessTypePodcast(response)
        }

        if (this.el.dataset.type === 'event') {
            return this.handleSuccessTypeEvent(response)
        }

        if (this.el.dataset.type === 'partner') {
            return this.handleSuccessTypePartner(response)
        }

        if (this.el.dataset.type === 'case-study') {
            return this.handleSuccessTypeCaseStudy(response)
        }

        if (this.el.dataset.type === 'solution') {
            return this.handleSuccessTypeSolution(response)
        }

        if (this.el.dataset.type === 'service') {
            return this.handleSuccessTypeService(response)
        }

        return this.handleError('missing data type')
    }

    handleSuccessTypeCard = (response) => {
        let resultsHTML = ''
        if (response.data) {
            resultsHTML +=
                '<div class="grid grid--4-col grid--2-col@medium grid--1-col@small">'
            response.data.forEach((el, i) => {
                const item = card(response.data[i])
                resultsHTML += item
            })
            resultsHTML += '</div>'
        }

        this.container.results.innerHTML = resultsHTML
        this.reEnableModules()
        this.scrollToResultsTop()
    }

    handleSuccessTypePodcast = (response) => {
        let resultsHTML = ''
        if (response.data) {
            resultsHTML +=
                '<div class="grid grid--4-col grid--3-col@medium grid--1-col@small">'
            response.data.forEach((el, i) => {
                const item = podcast(response.data[i])
                resultsHTML += item
            })
            resultsHTML += '</div>'
        }

        this.container.results.innerHTML = resultsHTML
        this.reEnableModules()
        this.scrollToResultsTop()
    }

    handleSuccessTypeEvent = (response) => {
        let resultsHTML = ''
        if (response.data) {
            resultsHTML += '<div class="grid grid--2-col grid--1-col@medium">'
            response.data.forEach((el, i) => {
                const item = event(response.data[i])
                resultsHTML += item
            })
            resultsHTML += '</div>'
        }

        this.container.results.innerHTML = resultsHTML
        this.reEnableModules()
        this.scrollToResultsTop()
    }

    handleSuccessTypePartner = (response) => {
        let resultsHTML = ''
        if (response.data) {
            resultsHTML +=
                '<div class="grid grid--4-col grid--2-col@medium grid--1-col@small">'
            response.data.forEach((el, i) => {
                const item = partner(response.data[i])
                resultsHTML += item
            })
            resultsHTML += '</div>'
        }

        this.container.results.innerHTML = resultsHTML
        this.reEnableModules()
        this.scrollToResultsTop()
    }

    handleSuccessTypeCaseStudy = (response) => {
        let resultsHTML = ''
        if (response.data) {
            resultsHTML +=
                '<div class="display display--3-col display--2-col@large display--1-col@small">'
            response.data.forEach((el, i) => {
                const item = caseStudy(response.data[i])
                resultsHTML += item
            })
            resultsHTML += '</div>'
        }

        this.container.results.innerHTML = resultsHTML
        this.reEnableModules()
        this.scrollToResultsTop()
    }

    handleSuccessTypeSolution = (response) => {
        let resultsHTML = ''
        if (response.data) {
            resultsHTML +=
                '<div class="display display--3-col display--2-col@large display--1-col@small">'
            response.data.forEach((el, i) => {
                const item = solution(response.data[i])
                resultsHTML += item
            })
            resultsHTML += '</div>'
        }

        this.container.results.innerHTML = resultsHTML
        this.reEnableModules()
        this.scrollToResultsTop()
    }

    handleSuccessTypeService = (response) => {
        let resultsHTML = ''
        if (response.data) {
            resultsHTML +=
                '<div class="display display--3-col display--2-col@large display--1-col@small">'
            response.data.forEach((el, i) => {
                const item = service(response.data[i])
                resultsHTML += item
            })
            resultsHTML += '</div>'
        }

        this.container.results.innerHTML = resultsHTML
        this.reEnableModules()
        this.scrollToResultsTop()
    }

    reEnableModules = () => {
        _('gtm-click')
            .nodesFrom(this.container.results)
            .map((el) => {
                el.addEventListener('click', () => {
                    makeGtmCall(el)
                })
            })
        _('', 'entrance')
            .nodesFrom(this.container.results)
            .map((el) => new EntranceTransition(el))

        _('image', 'lazyload')
            .nodesFrom(this.container.results)
            .map((el) => new LazyLoad(el))

        this.input = {
            els: _('input', 'element').nodesFrom(this.el),
            endpoint: _('endpoint-input').nodeFrom(this.el),
            search: _('search-input').nodeFrom(this.el),
            clear: _('clear-form').nodeFrom(this.el),
        }
    }

    handleNoResults = () => {
        this.container.noResults.classList.remove('u-hidden')
        this.hideLoading()
    }

    handleError = (err, defaultMessage = this.message.error) => {
        console.warn(err)
        this.hideLoading()

        let errorHTML = `<p>${defaultMessage}</p>`
        if (err && err.errors) {
            err.errors.map((el) => {
                errorHTML += `<p>${el}</p>`
            })
        }
        this.container.results.innerHTML = errorMessageContainer(errorHTML)
        Bugsnag.notify(
            new Error(`
                    ${errorHTML},
                    Endpoint: ${this.input.endpoint.value}`),
        )
    }
}
